/**
 * Chart state for saved reports.
 *
 * The charts integration exports and restores itself correctly on a plain grid. Pivoting
 * breaks that: its own export processor is registered later, and while pivot is active it
 * replaces the whole blob with the snapshot it cached when pivot was switched on. A report
 * saved while pivoted comes back with a stale chart, or none.
 *
 * So we capture the chart beside the blob and put it back by hand, the same way pivotState
 * handles the pivot model.
 *
 * After a MUI bump, `npm run test:upstream` says whether this is still needed.
 */

import type { SummaryReportChartState } from '../types.ts';

/** One entry in a chart's dimension or value selection. */
interface ChartFieldSelection {
    field: string;
    hidden?: boolean;
}

/** The chart slice of grid state: the field selection, and nothing else. */
interface ChartsIntegrationState {
    activeChartId?: string;
    charts?: Record<
        string,
        { dimensions?: ChartFieldSelection[]; values?: ChartFieldSelection[] } | undefined
    >;
}

/** What the integration keeps in React context for one chart, as far as we care. */
export interface ChartContextEntry {
    synced?: boolean;
    type?: string;
    configuration?: Record<string, string | number | boolean | null>;
}

/** The integration's context state, keyed by chart id. */
export type ChartContextLookup = Record<string, ChartContextEntry>;

/**
 * The slice of the grid api a restore needs.
 *
 * Described structurally rather than imported from MUI, so the unit specs can drive it
 * without standing up a grid. Everything here is public api.
 */
export interface ChartRestoreApi {
    getColumn: (field: string) => unknown;
    setChartType: (chartId: string, type: string) => void;
    setChartSynchronizationState: (chartId: string, synced: boolean) => void;
    setActiveChartId: (chartId: string) => void;
    updateChartDimensionsData: (chartId: string, dimensions: ChartFieldSelection[]) => void;
    updateChartValuesData: (chartId: string, values: ChartFieldSelection[]) => void;
}

/**
 * How long a restore waits for a saved field to become a column.
 *
 * Pivot-generated columns are built from the data, so they arrive a beat after the pivot
 * mode. A field can also be gone for good, when the report's filter no longer returns the
 * rows that produced it. The two look identical from here; waiting tells them apart.
 */
export const CHART_FIELD_WAIT_MS = 1500;

const copyFields = (fields: ChartFieldSelection[] | undefined): ChartFieldSelection[] =>
    (fields ?? []).map((item) => ({ ...item }));

/**
 * The chart, from the two places it lives: the grid's state and the integration's context.
 *
 * Returns `undefined` when there is no chart worth saving, which is how a save records "no
 * chart" and clears whatever the report held before.
 *
 * `titles` is a third place, and the only one that is ours — a name the user typed, which
 * the integration knows nothing about.
 */
export function captureChartState(
    chartsIntegration: ChartsIntegrationState | undefined,
    chartContext: ChartContextLookup | undefined,
    titles?: Record<string, string>
): SummaryReportChartState | undefined {
    const charts = chartsIntegration?.charts;
    if (!charts) return undefined;

    const captured: SummaryReportChartState = {
        activeChartId: chartsIntegration?.activeChartId ?? '',
        charts: {},
        context: {},
    };

    Object.entries(charts).forEach(([chartId, chart]) => {
        const dimensions = copyFields(chart?.dimensions);
        const values = copyFields(chart?.values);

        // Both halves or neither: the integration blanks the pane as soon as either is
        // empty, so a half-selection is a chart nobody can see.
        if (dimensions.length === 0 || values.length === 0) return;

        const context = chartContext?.[chartId];
        captured.charts[chartId] = { dimensions, values };
        captured.context[chartId] = {
            synced: context?.synced,
            type: context?.type,
            configuration: context?.configuration ? { ...context.configuration } : undefined,
        };

        // A blank name is how the user asks for the generated description back, so it is
        // saved as no name rather than as an empty string.
        const title = titles?.[chartId]?.trim();
        if (title) {
            captured.titles = { ...captured.titles, [chartId]: title };
        }
    });

    return Object.keys(captured.charts).length > 0 ? captured : undefined;
}

/** Whether a captured chart has anything to restore. */
export function isChartConfigured(chartState: SummaryReportChartState | undefined): boolean {
    return Object.keys(chartState?.charts ?? {}).length > 0;
}

/**
 * Saved fields that aren't columns on the grid right now.
 *
 * Empty means the restore can go ahead. Non-empty means either the grid hasn't caught up
 * yet, or those fields left with the data that produced them.
 */
export function getMissingChartFields(
    api: Pick<ChartRestoreApi, 'getColumn'>,
    chartState: SummaryReportChartState | undefined
): string[] {
    return Object.values(chartState?.charts ?? {})
        .flatMap((chart) => [...chart.dimensions, ...chart.values])
        .map((item) => item.field)
        .filter((field) => !api.getColumn(field));
}

/**
 * Puts a saved chart on the grid, and takes down any chart the report doesn't have.
 *
 * `registeredChartIds` is what the grid currently holds a chart for. Ids the report doesn't
 * name are cleared, so loading a chartless report dismisses what's on screen.
 *
 * Fields with no column are dropped and returned rather than handed over: the integration
 * would prune them on the next column change anyway, and the caller can report what went.
 */
export function applyChartState(
    api: ChartRestoreApi,
    setChartState: (chartId: string, state: ChartContextEntry) => void,
    chartState: SummaryReportChartState | undefined,
    registeredChartIds: readonly string[]
): string[] {
    const dropped: string[] = [];
    const keepExisting = (fields: ChartFieldSelection[]) =>
        fields.filter((item) => {
            if (api.getColumn(item.field)) return true;
            dropped.push(item.field);
            return false;
        });

    const chartIds = new Set([...registeredChartIds, ...Object.keys(chartState?.charts ?? {})]);

    chartIds.forEach((chartId) => {
        const saved = chartState?.charts?.[chartId];
        const context = chartState?.context?.[chartId];

        // Type before fields: the chart type sets how many dimensions and values are
        // allowed, and the integration trims the selection to those limits on arrival.
        if (context?.type) {
            api.setChartType(chartId, context.type);
        }
        if (context?.configuration) {
            setChartState(chartId, { configuration: context.configuration });
        }
        if (context?.synced !== undefined) {
            api.setChartSynchronizationState(chartId, context.synced);
        }

        api.updateChartDimensionsData(chartId, keepExisting(saved?.dimensions ?? []));
        api.updateChartValuesData(chartId, keepExisting(saved?.values ?? []));
    });

    if (chartState?.activeChartId) api.setActiveChartId(chartState.activeChartId);

    return dropped;
}
